import React from 'react';
import { Card, CardContent } from './ui/card';
import { 
  Wrench, HardDrive, ShieldCheck, Database, Wifi, Cpu, 
  TrendingUp, Monitor, Lightbulb, Laptop, Printer, Server 
} from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Wrench: Wrench,
  HardDrive: HardDrive,
  ShieldCheck: ShieldCheck,
  Database: Database,
  Wifi: Wifi,
  Cpu: Cpu,
  TrendingUp: TrendingUp,
  Monitor: Monitor,
  Lightbulb: Lightbulb,
  Laptop: Laptop,
  Printer: Printer,
  Server: Server
};

const ServicesSection = () => {
  return (
    <section id="services" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-semibold text-[#2563eb] bg-[#2563eb]/10 px-4 py-1.5 rounded-full mb-4">
            What We Do
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#1e1919] mb-4">
            Our Services
          </h2>
          <p className="text-[#736c64] max-w-2xl mx-auto">
            Comprehensive IT support and electronics services to keep your technology running smoothly. From repairs to custom builds, we've got you covered.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || HardDrive;
            const colors = [
              { bg: 'bg-[#2563eb]/10', border: 'border-[#2563eb]/20', icon: 'text-[#2563eb]' },
              { bg: 'bg-[#10b981]/10', border: 'border-[#10b981]/20', icon: 'text-[#10b981]' },
              { bg: 'bg-[#f59e0b]/10', border: 'border-[#f59e0b]/20', icon: 'text-[#f59e0b]' },
              { bg: 'bg-[#06b6d4]/10', border: 'border-[#06b6d4]/20', icon: 'text-[#06b6d4]' },
              { bg: 'bg-[#8b5cf6]/10', border: 'border-[#8b5cf6]/20', icon: 'text-[#8b5cf6]' },
              { bg: 'bg-[#ec4899]/10', border: 'border-[#ec4899]/20', icon: 'text-[#ec4899]' }
            ];
            const colorScheme = colors[index % colors.length];

            return (
              <Card
                key={service.id}
                className={`group border ${colorScheme.border} bg-white hover:shadow-lg transition-all duration-300 cursor-pointer hover:border-[#2563eb]/30`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardContent className="p-5">
                  <div className={`w-12 h-12 rounded-xl ${colorScheme.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className={`w-6 h-6 ${colorScheme.icon}`} />
                  </div>
                  <h3 className="text-base font-semibold text-[#1e1919] mb-2">
                    {service.title}
                  </h3>
                  <p className="text-[#736c64] text-sm leading-relaxed line-clamp-2">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div className="mt-12 bg-gradient-to-r from-[#1e1919] to-[#374151] rounded-2xl p-8 lg:p-12 text-center relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-[#2563eb]/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#2563eb]/10 rounded-full translate-x-1/4 translate-y-1/4"></div>
          
          <div className="relative z-10">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              Need IT Support?
            </h3>
            <p className="text-white/80 max-w-2xl mx-auto mb-6">
              Whether it's a computer repair, virus removal, data recovery, or setting up your home network - our expert technicians are here to help. Quick turnaround and reliable service guaranteed.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#2563eb]">24+</div>
                <div className="text-sm text-white/70">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#10b981]">Fast</div>
                <div className="text-sm text-white/70">Turnaround</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#f59e0b]">100%</div>
                <div className="text-sm text-white/70">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
